import React, { FC, useEffect, useRef, useState } from 'react';
import { useTrainingInitLazyQuery } from '~/data';
import { select } from '~/lib/select';
import { getLmsAdapter, logLmsLanguage } from '~/lms';
import { NotFound } from '~/ui/not-found/NotFound';
import { Spinner } from '~/ui/spinner/Spinner';
import { useTrainingParams } from '../useTrainingParams';
import { processData } from './processData';
import { syncLearnerLanguageFromLms } from './syncLearnerLanguageFromLms';
import { TrainingState } from './TrainingState';
import { useTrainingStore } from './useTrainingStore';
import { markAppReload } from '~/lib/reloadDiagnostics';

const selector = (s: TrainingState) =>
  select(s, 'textLocale', 'mediumLocale', 'subtitleLocale');

export const InitTrainingState: FC = ({ children }) => {
  const { learnerTrainingId } = useTrainingParams();
  const [getTrainingInit, { data, refetch, error }] =
    useTrainingInitLazyQuery();
  const [done, setDone] = useState(false);
  const lmsSyncedForTrainingRef = useRef<string | null>(null);
  const initDataAppliedForTrainingRef = useRef<string | null>(null);
  const launchSyncGenerationRef = useRef(0);
  const lmsSyncInProgressRef = useRef(false);
  const { textLocale, mediumLocale, subtitleLocale } =
    useTrainingStore(selector);

  useEffect(() => {
    setDone(false);
    lmsSyncedForTrainingRef.current = null;
    initDataAppliedForTrainingRef.current = null;
    launchSyncGenerationRef.current += 1;
  }, [learnerTrainingId]);

  useEffect(() => {
    // This will automatically call the API back when the language is changed,
    // ensuring that the navigation menu is updated.
    //
    // Skip while on onboarding: LMS/browser auto-detect updates `textLocale`
    // locally before the server catches up — refetching here would reload
    // stale `en` from trainingInit and reset the language picker.
    const isOnboarding =
      window.location.pathname.split('/').pop() === 'onboarding';

    if (refetch && data?.learnerTraining && !isOnboarding) {
      if (lmsSyncInProgressRef.current) return;

      if (
        data.learnerTraining.textLocale != textLocale ||
        data.learnerTraining.mediumLocale != mediumLocale ||
        data.learnerTraining.subtitleLocale != subtitleLocale
      ) {
        // Mark so the next boot does not misreport this as an external reload.
        markAppReload('locale-mismatch');
        document.location.reload();
      }

      if (
        data.learnerTraining.textLocale != textLocale ||
        data.learnerTraining.mediumLocale != mediumLocale ||
        data.learnerTraining.subtitleLocale != subtitleLocale
      ) {
        refetch();
      }
    }
  }, [textLocale, mediumLocale, subtitleLocale]);

  // Call the API when the learner training id is present or updated
  useEffect(() => {
    if (!learnerTrainingId) return;
    getTrainingInit({ variables: { learnerTrainingUuid: learnerTrainingId } });
  }, [learnerTrainingId]);

  // Process the API data and update the store when data changes
  useEffect(() => {
    let cancelled = false;
    const generation = ++launchSyncGenerationRef.current;

    if (!data || !learnerTrainingId) return;
    if (data.learnerTraining.id !== learnerTrainingId) return;

    if (lmsSyncedForTrainingRef.current === learnerTrainingId) {
      setDone(true);
      return;
    }

    // Seed store once per training from the initial query. Re-runs while LMS
    // sync is in flight must not overwrite locales sync may have just persisted.
    if (initDataAppliedForTrainingRef.current !== learnerTrainingId) {
      useTrainingStore.setState(processData(data));
      initDataAppliedForTrainingRef.current = learnerTrainingId;
    }

    void (async () => {
      lmsSyncInProgressRef.current = true;
      try {
        const lms = getLmsAdapter();
        logLmsLanguage('init-training launch sync', {
          adapter: lms.name,
          isAvailable: lms.isAvailable,
          learnerTrainingId
        });

        if (lms.isAvailable) {
          const synced = await syncLearnerLanguageFromLms(lms);
          if (cancelled || generation !== launchSyncGenerationRef.current) {
            return;
          }

          if (synced && refetch) {
            const result = await refetch();
            if (cancelled || generation !== launchSyncGenerationRef.current) {
              return;
            }

            if (result.data?.learnerTraining.id === learnerTrainingId) {
              useTrainingStore.setState(processData(result.data));
            }
          }
        }

        if (!cancelled && generation === launchSyncGenerationRef.current) {
          lmsSyncedForTrainingRef.current = learnerTrainingId;
        }
      } catch (error) {
        logLmsLanguage('init-training launch sync failed', {
          error: error instanceof Error ? error.message : String(error)
        });
      } finally {
        if (generation === launchSyncGenerationRef.current) {
          lmsSyncInProgressRef.current = false;
        }
      }

      if (!cancelled && generation === launchSyncGenerationRef.current) {
        setDone(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [data, learnerTrainingId, refetch]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const statusCode = (error?.networkError as any)?.statusCode;
  if (statusCode === 401 || statusCode === 404) return <NotFound />;

  if (!done) return <Spinner />;

  if (!children) return null;

  return <>{children}</>;
};
